import React, { useState, useEffect, useRef } from "react";
import "../../styles/TopBar.css";
import SwitchCompanyModal from "../Shared/SwitchCompanyModal";
import CompanyInfoModal from "../Shared/CompanyInfoModal";

function TopBar({
  user = {},
  company,
  onProfileClick,
  onToggleSidebar,
  onLogout,
  onSettings,
  apiKeys = [],
  currentApiKey = "",
  onCompanyChange,
  showSplashThen,
}) {
  const SHOW_TOPBAR_SEARCH = false;
  const SHOW_MAIL_ICON = false;
  const SHOW_NOTIF_ICON = false;
  const SHOW_PROFILE_ITEMS = false;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSwitchCompanyModal, setShowSwitchCompanyModal] = useState(false);
  const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        else if (el.msRequestFullscreen) el.msRequestFullscreen();
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {}
  };

  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const clearSearch = (e) => {
    e && e.preventDefault();
    setSearchValue("");
    if (searchRef.current) searchRef.current.focus();
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setShowDropdown(false);
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  return (
    <header className="topbar">
      <div
        className="topbar-left"
        style={{ display: "flex", alignItems: "center", gap: 6 }}
      >
        <button
          className="menu-btn"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect y="1" width="20" height="2" rx="1" fill="currentColor" />
            <rect y="6" width="20" height="2" rx="1" fill="currentColor" />
            <rect y="11" width="20" height="2" rx="1" fill="currentColor" />
          </svg>
        </button>

        <button
          className="menu-btn"
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          aria-label="Fullscreen"
          onClick={toggleFullscreen}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <>
              <path
                d="M8 3H5a2 2 0 0 0-2 2v3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 3h3a2 2 0 0 1 2 2v3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 21H5a2 2 0 0 1-2-2v-3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 21h3a2 2 0 0 0 2-2v-3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          </svg>
        </button>
        {company && company.name && (
          <div
            className="topbar-company"
            aria-label={`Current company ${company.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowCompanyInfoModal(true);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowCompanyInfoModal(true);
              }
            }}
          >
            {company.name}
          </div>
        )}

        {SHOW_TOPBAR_SEARCH && (
          <div className="search-wrapper">
            <div
              className={`search-box compact ${
                searchFocused ? "focused" : ""
              } ${searchValue ? "has-value" : ""}`}
              role="search"
            >
              <input
                ref={searchRef}
                className="search-input"
                type="search"
                placeholder="Search"
                aria-label="Search"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />

              {searchValue.length > 0 && (
                <button
                  className="search-clear"
                  aria-label="Clear search"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearSearch}
                  title="Clear"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}

              <span className="search-icon" aria-hidden>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M21 21l-4.35-4.35"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="topbar-search" />

      <div className="header-right">
        <div className="topbar-actions">
          {SHOW_MAIL_ICON && (
            <div
              className="relative topbar-icon mail-icon no-hover"
              aria-label="Messages"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="14"
                  rx="3"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  fill="white"
                />
                <path
                  d="M3.8 6.5L12 12.8l8.2-6.3"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          {SHOW_NOTIF_ICON && (
            <div className="topbar-icon no-hover" aria-label="Notifications">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <path
                  d="M13.73 21a2 2 0 01-3.46 0"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>

        <div ref={dropdownRef} style={{ position: "relative" }}>
          <div
            className="profile-menu"
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown((s) => !s);
            }}
            style={{ cursor: "pointer" }}
            aria-haspopup="true"
            aria-expanded={showDropdown}
          >
            <div className="profile-avatar">
              {user && user.avatar ? (
                <img src={user.avatar} alt={user.name || "avatar"} />
              ) : user.name ? (
                user.name.charAt(0).toUpperCase()
              ) : (
                "U"
              )}
            </div>
          </div>

          <div
            className={`profile-dropdown ${showDropdown ? "open" : "closed"}`}
            role="menu"
            aria-hidden={!showDropdown}
          >
            <div className="profile-hello">
              HELLO {user && user.name ? user.name.toUpperCase() : "USER"}
            </div>

            <div className="profile-list">
              {SHOW_PROFILE_ITEMS && (
                <>
                  <button
                    className="profile-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      if (typeof onProfileClick === "function")
                        onProfileClick();
                    }}
                  >
                    <span className="item-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <path
                          d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="7"
                          r="4"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span>Profile</span>
                  </button>

                  <button
                    className="profile-item"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span className="item-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <path
                          d="M13 2L3 14h9l-1 8L21 10h-9l1-8z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span>Activities</span>
                  </button>

                  <button
                    className="profile-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      if (typeof onSettings === "function") onSettings();
                    }}
                  >
                    <span className="item-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09c0-.69-.39-1.31-1-1.51a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06c.46-.46.6-1.13.33-1.82a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09c.69 0 1.31-.39 1.51-1a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06c.46.46 1.13.6 1.82.33.61-.22 1-0.83 1-1.51V3a2 2 0 0 1 4 0v.09c0 .69.39 1.31 1 1.51.69.26 1.36.12 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06c-.46.46-.6 1.13-.33 1.82.22.61.83 1 1.51 1H21a2 2 0 0 1 0 4h-.09c-.69 0-1.31.39-1.51 1z" />
                      </svg>
                    </span>

                    <span>Settings</span>
                  </button>
                </>
              )}

              {apiKeys.length > 1 && (
                <button
                  className="profile-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    setShowSwitchCompanyModal(true);
                  }}
                >
                  <span className="item-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <path
                        d="M3 7v6a9 9 0 0 0 9 9v-6"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 7a9 9 0 0 0-9-9v6"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 2v6l4-4"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>Switch Company</span>
                </button>
              )}

              <div className="divider" />

              <button
                className="profile-item logout"
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onLogout === "function") onLogout();
                }}
              >
                <span className="item-icon">
                  <span style={{ color: "#ef4444" }}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <path
                        d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
                        stroke="#ef4444"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 17l5-5-5-5"
                        stroke="#ef4444"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="21"
                        y1="12"
                        x2="9"
                        y2="12"
                        stroke="#ef4444"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
                <span className="item-text">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <SwitchCompanyModal
        isOpen={showSwitchCompanyModal}
        onClose={() => setShowSwitchCompanyModal(false)}
        apiKeys={apiKeys}
        currentApiKey={currentApiKey}
        onCompanyChange={onCompanyChange}
        showSplashThen={showSplashThen}
      />

      <CompanyInfoModal
        isOpen={showCompanyInfoModal}
        onClose={() => setShowCompanyInfoModal(false)}
        company={company}
        onSwitch={() => {
          setShowCompanyInfoModal(false);
          setShowSwitchCompanyModal(true);
        }}
      />
    </header>
  );
}

export default TopBar;
